import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import type { KeycloakUserDto, Paginated, UserResponse } from '../../../api/types';
import { usersKeys } from '../../users/api/keys';
import { keycloakKeys } from './keys';

export interface KeycloakOrphansParams extends Record<string, unknown> {
  limit?: number;
  offset?: number;
  search?: string;
}

interface LinkKeycloakUserInput {
  keycloakId: string;
  roleIds: string[];
}

export function useKeycloakOrphans(params: KeycloakOrphansParams = {}) {
  return useQuery<Paginated<KeycloakUserDto>>({
    queryKey: keycloakKeys.list({ ...params, linked: false }),
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<KeycloakUserDto>>('/admin/keycloak/users', {
        params: { ...params, linked: false },
      });
      return data;
    },
    staleTime: 30_000,
  });
}

export function useLinkKeycloakUser() {
  const queryClient = useQueryClient();

  return useMutation<UserResponse, Error, LinkKeycloakUserInput>({
    mutationFn: async ({ keycloakId, roleIds }) => {
      const { data } = await apiClient.post<UserResponse>(
        `/admin/keycloak/users/${encodeURIComponent(keycloakId)}/link`,
        { roleIds },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keycloakKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
  });
}
